import ReactEcharts from 'echarts-for-react'

import { BasicTable, ContentCard } from '@/components/customComponents'

import {
  pieChartOptions,
  stackedBarChartOptions,
} from '@/constants/CHART_CONSTANTS'
import { useDiscountsItems } from '@/hooks'
import { useReduxSalesLensFilter } from '@/store'
import { dateToString } from '@/utils'

interface DiscountTabProps {
  itemName: string
}

const STACKED_BAR_CHART_DATA = {
  rawData: [
    [120, 80, 65, 30],
    [100, 70, 55, 20],
    [80, 60, 45, 10],
    [40, 30, 25, 5],
  ],
  seriesNames: ['Students', 'Young Professionals', 'Families', 'Employees'],
  xAxisNames: ['Loyalty', 'Campaign', 'Operational', 'Employee'],
}

export const DiscountTab = ({ itemName }: DiscountTabProps) => {
  // Get filter state from Redux (sales lens specific)
  const { selectedBranch, date, sizeFilter, temperatureFilter } =
    useReduxSalesLensFilter()

  const {
    discountsItemsData,
    BASE_DISCOUNTS_ITEMS_COLUMNS,
    loading: discountsItemsLoading,
  } = useDiscountsItems(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName,
    sizeFilter,
    temperatureFilter
  )

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <ContentCard
          title="Discount Usage by Persona"
          showViewMore={false}
          onViewMore={() => {}}
          loading={false}
          className="col-span-6"
          isBeta
        >
          <ReactEcharts
            option={getStackedBarChartData(STACKED_BAR_CHART_DATA)}
          />
        </ContentCard>
        <ContentCard
          title="Discount Share in Revenue"
          showViewMore={false}
          onViewMore={() => {}}
          loading={false}
          className="col-span-3"
          isBeta
        >
          <ReactEcharts
            option={getPieChartData(
              {
                small: 920,
                medium: 800,
                large: 350,
                employee: 100,
              },
              ['Small', 'Medium', 'Large', 'Employee']
            )}
          />
        </ContentCard>
        <ContentCard
          title="Discount Share in Transactions"
          showViewMore={false}
          onViewMore={() => {}}
          loading={false}
          className="col-span-3"
          isBeta
        >
          <ReactEcharts
            option={getPieChartData(
              {
                loyalty: 120,
                campaign: 85,
                operational: 65,
                employee: 30,
              },
              ['Loyalty', 'Campaign', 'Operational', 'Employee']
            )}
          />
        </ContentCard>
      </div>

      <div>
        <BasicTable
          title="Discount Types"
          data={discountsItemsData}
          columns={BASE_DISCOUNTS_ITEMS_COLUMNS}
          loading={discountsItemsLoading}
          enablePagination={true}
          itemsPerPage={5}
        />
      </div>
    </div>
  )
}

const getStackedBarChartData = (primaryData: {
  rawData: number[][]
  seriesNames: string[]
  xAxisNames: string[]
}) => {
  return stackedBarChartOptions(
    primaryData.rawData,
    primaryData.seriesNames,
    primaryData.xAxisNames,
    '40%'
  )
}

const getPieChartData = (dataObj: Record<string, number>, labels: string[]) => {
  const data = labels.map((label) => dataObj[label.toLowerCase()] ?? 0)
  return pieChartOptions(data, labels)
}
